'use client';

import React, { useState } from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { InfoIcon } from '@depop/web-ui-kit/Icons/InfoIcon';
import { useResponsive } from '@depop/web-ui-kit/useResponsive';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ButtonMinimal } from '@/components/ButtonMinimal';
import { LocaleCurrencyFormat } from '@/components/LocaleCurrencyFormat';
import { Price } from '@/components/Price';
import { ProductPriceBreakdownModal } from '@/components/ProductPriceBreakdownModal';
import { SecondaryPriceLine } from '@/components/SecondaryPriceLine';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { type DisplayPrice } from '@/modules/price/aliases';
import {
  getHeadlineDescriptionKey,
  shouldShowPriceBreakdown,
} from '@/modules/price/helpers/displayPriceRules';
import { type ProductPrice } from '@/modules/product/types';

type Props = {
  displayPrice: DisplayPrice; // Every price rendered here comes from displayPrice.
  price: ProductPrice; // Needed for the breakdown modal's contents.
};

export function PriceWithMarketplaceFee({ displayPrice, price }: Props) {
  const tCommon = useTranslations('common');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isMaxMedium: isMobile } = useResponsive();

  function toggleModal() {
    setIsModalOpen((isOpen) => !isOpen);
  }

  const descriptionKey = getHeadlineDescriptionKey(
    displayPrice.headline_price_components
  );
  const showBreakdownButton = shouldShowPriceBreakdown(displayPrice);
  const { secondary_price: secondaryPrice } = displayPrice;

  const strikethroughPrice = displayPrice.strikethrough_price ? (
    <Caption
      variant="lg-regular"
      className={clsx(styles.originalPrice, styles.originalPriceStrikethrough)}
    >
      <LocaleCurrencyFormat
        currencyCode={displayPrice.strikethrough_price.currency as CurrencyCode}
        numberToFormat={displayPrice.strikethrough_price.amount}
      />
    </Caption>
  ) : null;

  return (
    <div>
      <div className={styles.priceWithFeeWrapper}>
        <Price
          currencyCode={displayPrice.headline_price.currency as CurrencyCode}
          price={displayPrice.headline_price.amount}
          ariaDescription={tCommon('PriceWithFee')}
        />
        {secondaryPrice && strikethroughPrice}
        {descriptionKey && (
          <Caption
            variant="lg-regular"
            className={styles.inclFeesAndShippingText}
          >
            {tCommon(descriptionKey)}
          </Caption>
        )}
        {showBreakdownButton && (
          <ButtonMinimal
            className={styles.infoIconButton}
            onClick={toggleModal}
            aria-label={tCommon('LearnMore')}
          >
            <InfoIcon size={isMobile ? 16 : 20} />
          </ButtonMinimal>
        )}
      </div>
      {!secondaryPrice && strikethroughPrice}
      {secondaryPrice && <SecondaryPriceLine secondaryPrice={secondaryPrice} />}
      {showBreakdownButton && (
        <ProductPriceBreakdownModal
          price={price}
          isOpen={isModalOpen}
          setOpen={toggleModal}
        />
      )}
    </div>
  );
}
